// Pricing engine v2: tarifa base $4,500 (1-200 pax) + matriz escalonada + carpas modulares 20m/25m.

export const MAX_GUESTS = 750;

export const EVENT_TYPES = ["Boda", "Graduación", "Corporativo", "Aniversario", "Otro"];

export const CATEGORIES = [
    { key: "wedding_planner", label: "Wedding Planner" },
    { key: "catering", label: "Catering" },
    { key: "decoracion", label: "Decoración" },
    { key: "musica", label: "Música" },
    { key: "fotografia", label: "Fotografía" },
    { key: "video", label: "Video" },
];

// Categorías bloqueadas cuando WP es "Todo Incluido" (la carpa/estructura se bloquea aparte)
export const WP_LOCKED_CATEGORIES = ["catering", "decoracion", "musica", "fotografia", "video"];

export const LOCATIONS = [
    { key: "capilla", label: "Capilla", description: "Ceremonia en la capilla colonial de la Hacienda. Incluida por defecto — puedes desmarcarla si no la necesitas." },
    { key: "jardines", label: "Jardines", description: "Ceremonia al aire libre entre eucaliptos y jardines." },
    { key: "iglesia_externa", label: "Iglesia Externa", description: "Ceremonia en iglesia externa; recepción en Hacienda." },
];

export const BASE_INFRASTRUCTURE = [
    { icon: "sparkles", label: "La Isla del Reservorio", description: "Incluida en todos los paquetes: acceso exclusivo a la isla del reservorio para ceremonia, brindis o sesión fotográfica." },
    { icon: "sparkles", label: "Jardines e iluminación nocturna", description: "Jardines decorativos y sistema de iluminación nocturna perimetral de la Hacienda." },
    { icon: "sparkles", label: "Baños con personal", description: "Batería de baños con personal de limpieza y mantenimiento permanente durante el evento." },
    { icon: "shield", label: "Personal de control general", description: "Limpieza, baños y coordinación de parqueadero. NO incluye seguridad privada — disponible como servicio adicional con proveedor de referencia." },
    { icon: "camera", label: "Locaciones de sesión fotográfica", description: "Acceso a jardines decorativos, reservorio, isla y cascada para producción fotográfica." },
    { icon: "car", label: "Parqueadero privado", description: "Parqueadero con control de acceso y personal de guía." },
    { icon: "sparkles", label: "Suite de novios", description: "Espacio de retoque y descanso para novios y comitiva." },
];

export const FAQ_ITEMS = [
    { q: "¿Qué pasa si el clima no acompaña?", a: "El cotizador dimensiona automáticamente una carpa modular según tu aforo. Puedes elegir entre estructuras de 20m o 25m de ancho; el flete está incluido como valor estimado." },
    { q: "¿Cómo se dimensiona la carpa principal?", a: "Se sugiere aumentar 1 módulo por cada 50 invitados o fracción. El sistema selecciona automáticamente la carpa que mejor responde a tu rango de personas y calcula los m² requeridos." },
    { q: "¿Cuándo se bloquea la fecha?", a: "La reserva de fecha únicamente se confirma con depósito y contrato firmado. Este cotizador es referencial: te ayuda a diseñar tu evento y agendar la visita técnica." },
    { q: "¿Puedo modificar mi cotización después de enviarla?", a: "Sí. Tu cotización se guarda con un ID único de sesión. Si envías nuevos cambios usando el mismo dispositivo/navegador, actualizamos el registro sin duplicarlo." },
    { q: "¿Qué incluye el paquete 'Todo Incluido' del Wedding Planner?", a: "Cuando eliges el paquete Todo Incluido, la planner asume la gestión integral bajo un único contrato: catering, decoración, música, fotografía, video y estructuras se cierran a través de ella." },
];

export const PRICING_DISCLAIMER = "Los valores mostrados son precios referenciales estimados para el presupuesto inicial. Cada proveedor deberá realizar una recotización formal de acuerdo a las necesidades y especificaciones requeridas por el cliente.";

export function isAllInclusiveWP(state, config) {
    const wpId = state.providers?.wedding_planner;
    if (!wpId) return false;
    const wp = config.providers.find((p) => p.id === wpId);
    return !!wp?.all_inclusive;
}

// ---- Temporadas (recargo % sobre tarifa base) ----------------------------------

export function detectSeason(state, pricing) {
    const high = pricing?.high_months || [];
    let months = [];
    if (state.date_mode === "exact" && state.exact_date) {
        months = [new Date(state.exact_date + "T12:00:00").getMonth() + 1];
    } else if (state.date_mode === "months") {
        months = state.tentative_months || [];
    }
    if (!months.length) return "baja";
    return months.some((m) => high.includes(m)) ? "alta" : "baja";
}

// ---- Carpas modulares ---------------------------------------------------------

export function computeCarpa(guests, widthKey, pricing) {
    const spec = pricing?.[`carpa_${widthKey}`];
    if (!spec || !guests) return null;
    const gpm = spec.guests_per_module || 50;
    const modules = Math.max(1, Math.ceil(guests / gpm));
    if (spec.max_modules && modules > spec.max_modules) return null;
    const area = modules * (spec.module_area_m2 || 0);
    const price = modules * (spec.module_price || 0);
    const flete = spec.flete || 0;
    return {
        width: widthKey, modules, area_m2: area, price, flete,
        total: price + flete, price_per_m2: spec.price_per_m2 || 0,
    };
}

export function recommendedCarpaWidth(guests, pricing) {
    const c20 = computeCarpa(guests, "20", pricing);
    const c25 = computeCarpa(guests, "25", pricing);
    if (!c20) return "25";
    if (!c25) return "20";
    return c20.total <= c25.total ? "20" : "25";
}

export function resolveCarpa(state, pricing) {
    const mode = state.carpa_mode || "auto";
    if (mode === "none") return null;
    if (mode === "manual") {
        const m2 = Math.max(0, Number(state.carpa_m2) || 0);
        if (!m2) return null;
        const spec = pricing?.carpa_20 || {};
        const rate = spec.price_per_m2 || 4;
        const flete = spec.flete || 0;
        return { width: "manual", modules: null, area_m2: m2, price: m2 * rate, flete, total: m2 * rate + flete, price_per_m2: rate };
    }
    const guests = Math.max(0, Number(state.guests) || 0);
    if (!guests) return null;
    const width = mode === "20" || mode === "25" ? mode : recommendedCarpaWidth(guests, pricing);
    return computeCarpa(guests, width, pricing)
        || computeCarpa(guests, width === "20" ? "25" : "20", pricing);
}

// ---- Personal operativo y matriz de invitados extra ---------------------------

export function staffIncluded(guests, pricing) {
    const tiers = pricing?.staff_tiers || [{ up_to: 450, staff: 6 }, { up_to: 750, staff: 8 }];
    const tier = tiers.find((t) => guests <= t.up_to) || tiers[tiers.length - 1];
    return tier?.staff || 6;
}

export function extraGuestRate(guests, pricing) {
    const tiers = pricing?.extra_guest_tiers || [];
    if (!tiers.length) return 15;
    const tier = tiers.find((t) => guests <= t.up_to) || tiers[tiers.length - 1];
    return tier.price_per_extra || 0;
}

// ---- Main ----------------------------------------------------------------------

function computeProviderAmount(provider, guests) {
    if (provider.price_mode === "per_guest") {
        return { amount: provider.price * guests, note: `${guests} pax × $${provider.price}` };
    }
    if (provider.price_mode === "per_module") {
        const modules = Math.max(1, Math.ceil(guests / (provider.module_capacity || 50)));
        return { amount: modules * provider.price, note: `${modules} módulo(s)` };
    }
    return { amount: provider.price, note: "" };
}

function buildExtraServiceLine(s, sel, guests) {
    if (s.type === "selector") {
        const opt = (s.options || [])[sel.option ?? 0];
        if (!opt) return null;
        return { key: `extra_${s.id}`, label: `${s.name} — ${opt.label}`, amount: opt.price || 0 };
    }
    if (s.type === "counter") {
        const qty = Math.max(0, Number(sel.qty) || 0);
        if (qty === 0) return null;
        return { key: `extra_${s.id}`, label: s.name, amount: qty * (s.unit_price || 0), note: `${qty} ${s.unit_label || "unidad(es)"} × $${s.unit_price}` };
    }
    if (s.type === "per_guest") {
        return { key: `extra_${s.id}`, label: s.name, amount: (s.per_guest_price || 0) * guests, note: `${guests} pax × $${s.per_guest_price}` };
    }
    return { key: `extra_${s.id}`, label: s.name, amount: s.price || 0 };
}

export function computeQuote(state, config) {
    const breakdown = { lines: [] };
    const guests = Math.max(0, Number(state.guests) || 0);

    if (guests >= 751) {
        breakdown.total = 0;
        breakdown.subtotal = 0;
        breakdown.mega_event = true;
        return breakdown;
    }

    const pricing = config.pricing || {};
    const providers = config.providers || [];
    const byId = Object.fromEntries(providers.map((p) => [p.id, p]));
    const wpAllInclusive = isAllInclusiveWP(state, config);

    // 1. Tarifa base + invitados adicionales
    if (guests > 0) {
        const included = pricing.base_guests_included || 200;
        breakdown.lines.push({
            key: "venue_base",
            label: `Tarifa base Hacienda (1–${included} invitados)`,
            amount: pricing.base_price || 4500,
        });
        if (guests > included) {
            const rate = extraGuestRate(guests, pricing);
            const extra = guests - included;
            breakdown.lines.push({
                key: "extra_guests",
                label: `Invitados adicionales (${extra} × $${rate})`,
                amount: extra * rate,
                note: "Matriz escalonada referencial — configurable en Backoffice",
            });
        }
        breakdown.staff = staffIncluded(guests, pricing);

        // Recargo por temporada alta (detección automática por fecha)
        const season = detectSeason(state, pricing);
        breakdown.season = season;
        const pct = pricing.high_season_surcharge_pct || 0;
        if (season === "alta" && pct > 0) {
            breakdown.lines.push({
                key: "season_surcharge",
                label: `Recargo temporada alta (${pct}%)`,
                amount: (pricing.base_price || 4500) * (pct / 100),
                note: "Aplicado sobre la tarifa base según la fecha elegida",
            });
        }
    }

    // 2. Carpa modular (estructura del salón)
    if (guests > 0) {
        const carpa = resolveCarpa(state, pricing);
        if (carpa) {
            breakdown.carpa = carpa;
            if (wpAllInclusive) {
                breakdown.carpa_included_in_wp = true;
            } else {
                const isManual = carpa.width === "manual";
                breakdown.lines.push({
                    key: "carpa",
                    label: isManual
                        ? `Carpa — metraje manual · ${carpa.area_m2} m²`
                        : `Carpa modular ${carpa.width}m — ${carpa.modules} módulo(s) · ${carpa.area_m2} m²`,
                    amount: carpa.price,
                    note: isManual
                        ? `$${carpa.price_per_m2}/m² · metraje ingresado por el cliente`
                        : `$${carpa.price_per_m2}/m² · se sugiere 1 módulo por cada 50 invitados o fracción`,
                });
                breakdown.lines.push({
                    key: "carpa_flete",
                    label: "Flete y transporte de estructura (estimado)",
                    amount: carpa.flete,
                });
            }
        }
    }

    // 3. Escenarios: la Isla está incluida en todos los paquetes (sin fee ni recargo)

    // 4. Proveedores Mix & Match
    for (const cat of CATEGORIES) {
        const pid = state.providers?.[cat.key];
        const p = pid ? byId[pid] : null;
        if (!p) continue;
        if (wpAllInclusive && WP_LOCKED_CATEGORIES.includes(cat.key)) continue;
        const calc = computeProviderAmount(p, guests);
        breakdown.lines.push({
            key: `prov_${cat.key}`,
            label: `${cat.label} — ${p.name}`,
            category: cat.label,
            provider_name: p.name,
            brand: p.brand || p.name,
            tier: p.tier,
            amount: calc.amount,
            note: calc.note,
            all_inclusive: p.all_inclusive || false,
        });
    }

    // 5. Servicios adicionales (Lámina 5)
    for (const s of (config.extra_services || [])) {
        if (s.active === false) continue;
        const sel = state.extra_services?.[s.id];
        if (!sel || !sel.on) continue;
        const line = buildExtraServiceLine(s, sel, guests);
        if (line) breakdown.lines.push(line);
    }

    const subtotal = breakdown.lines.reduce((sum, l) => sum + (l.amount || 0) + (l.surcharge || 0), 0);
    breakdown.subtotal = subtotal;
    breakdown.total = subtotal;
    breakdown.wp_all_inclusive = wpAllInclusive;
    return breakdown;
}
